import React, { useState, useEffect } from 'react';
import {
  Button,
  DatePicker,
  DialogContent,
  Input,
  palettes,
  Select,
  Theme,
  Textarea,
  useFlashMessage,
  SelectChangeEvent
} from '@indigo-ag/component-module';
import { MaterialUiPickersDate } from 'material-ui-pickers';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Credit, JudicialAction, JudicialActionFlag } from '@indigo-ag/schema';

import i18next from 'core/i18n';
import { EnhancedDialog, createStylesHook, CURRENCY_SYMBOL, flagTypeOptions, utils, ModalActionButtons } from 'shared';
import { JudicialActionsProps } from '../../JudicialActionsSection';
import { currency } from '../../../../../../../../../shared/utils';

export interface JudicialActionsModalProps extends JudicialActionsProps {
  closeModal: () => void;
  open: boolean;
  // TODO create type for function
  updateCredit: (newCreditInfo: Partial<Credit>) => Promise<unknown>;
}

const useStyles = createStylesHook((theme: Theme) => ({
  flexDocument: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  inputStyle: {
    marginRight: '32px !important'
  },
  size14Black: {
    fontSize: theme.typography.pxToRem(14),
    color: palettes.neutral['700']
  }
}));

export const JudicialActionsModal: React.FunctionComponent<JudicialActionsModalProps> = ({
  closeModal,
  open,
  judicialActionsInfo,
  updateCredit
}) => {
  /**
   * State
   */
  const [court, setCourt] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [actionType, setActionType] = useState('');
  const [causeValue, setCauseValue] = useState('');
  const [processNumber, setProcessNumber] = useState('');
  const [distributionDate, setDistributionDate] = useState(null);
  const [note, setNote] = useState('');
  const [flag, setFlag] = useState<JudicialActionFlag | null>(null);
  const [judicialActions, setJudicialActions] = useState<JudicialAction[]>(judicialActionsInfo?.judicialActions || []);

  const onCleanState = (): void => {
    setCourt('');
    setAuthorName('');
    setActionType('');
    setCauseValue('');
    setProcessNumber('');
    setDistributionDate(null);
    setNote('');
    setFlag(null);
  };

  useEffect(() => {
    onCleanState();
    setJudicialActions(judicialActionsInfo?.judicialActions || []);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Styles
   */
  const classes = useStyles();

  /**
   * Handlers
   */

  const handleCourt = (event: React.ChangeEvent<HTMLInputElement>): void => setCourt(event.target.value);

  const handleAuthorName = (event: React.ChangeEvent<HTMLInputElement>): void => setAuthorName(event.target.value);

  const handleActionType = (event: React.ChangeEvent<HTMLInputElement>): void => setActionType(event.target.value);

  const handleCauseValue = (event: React.ChangeEvent<HTMLInputElement>): void =>
    setCauseValue(currency.inputMaskCurrency(event.target.value, { currencySymbol: undefined }));

  const handleProcessNumber = (event: React.ChangeEvent<HTMLInputElement>): void =>
    setProcessNumber(event.target.value);

  const handleDistributionDate = (date: MaterialUiPickersDate): void => setDistributionDate(date);

  const handleNote = (event: React.ChangeEvent<HTMLTextAreaElement>): void => setNote(event.target.value);

  const handleFlag = (event: SelectChangeEvent<JudicialActionFlag>): void => setFlag(event.target.value);

  const handleAddJudicialAction = (): void => {
    setJudicialActions([
      ...judicialActions,
      {
        actionType,
        authorName,
        causeValue: Number(causeValue.replace(/\./g, '').replace(/,/g, '.')),
        court,
        distributionDate: distributionDate || undefined,
        flag: flag || undefined,
        observations: note,
        processNumber
      }
    ]);
    onCleanState();
  };

  const handleRemoveJudicialAction = (action: number) => {
    return () => {
      const newActions = judicialActions.filter((_, index) => index !== action);
      setJudicialActions(newActions);
    };
  };

  const renderBrazilCurrencySymbol = (): JSX.Element => (
    <span style={{ color: palettes.neutral['900'] }}>{CURRENCY_SYMBOL.BR}</span>
  );

  const isAddBtnDisable = !causeValue;

  const { addFlashMessage } = useFlashMessage();

  const updateInfo = () => {
    const newJudictialActions = [...judicialActions];
    if (!isAddBtnDisable) {
      newJudictialActions.push({
        actionType,
        authorName,
        causeValue: Number(causeValue.replace(/\./g, '').replace(/,/g, '.')),
        court,
        distributionDate: distributionDate || undefined,
        flag: flag || undefined,
        observations: note,
        processNumber
      });
    }
    return updateCredit({ judicialActions: utils.graphql.removeTypename(newJudictialActions) }).then(() => {
      addFlashMessage({
        content: `${i18next.t('judicialActions')} ${i18next.t('hasBeenUpdatedSuccessfully')}`,
        title: '',
        variant: 'success'
      });
    });
  };

  return (
    <EnhancedDialog open={open} onClose={closeModal} title={i18next.t('judicialActions')}>
      <DialogContent>
        <Input label={i18next.t('court')} onChange={handleCourt} value={court} variant="text" />
        <Input label={i18next.t('authorName')} onChange={handleAuthorName} value={authorName} variant="text" />
        <div className={classes.flexDocument}>
          <Input
            className={classes.inputStyle}
            label={i18next.t('actionType')}
            onChange={handleActionType}
            value={actionType}
            variant="text"
          />
          <Input
            label={i18next.t('causeValue')}
            onChange={handleCauseValue}
            renderBeforeInput={renderBrazilCurrencySymbol}
            value={causeValue}
            variant="text"
          />
        </div>
        <Input label={i18next.t('processNumber')} value={processNumber} onChange={handleProcessNumber} variant="text" />
        <DatePicker
          label={i18next.t('distributionDate')}
          name="distribution-date"
          onChange={handleDistributionDate}
          value={distributionDate}
        />
        <Textarea label={i18next.t('note')} onChange={handleNote} value={note} />
        <Select
          label={i18next.t('flag')}
          name="flag-options"
          onChange={handleFlag}
          options={flagTypeOptions}
          placeholder={i18next.t('pleaseSelect')}
          value={flag}
        />
        <Button
          disabled={!causeValue}
          onClick={handleAddJudicialAction}
          size="small"
          style={{ marginTop: 24 }}
          variant="secondary"
        >
          {i18next.t('add')}
        </Button>
        {judicialActions.length > 0 && <p className={classes.size14Black}>{i18next.t('judicialActions')}</p>}
        {judicialActions &&
          judicialActions.map((action, index: number) => (
            <div key={index} className={classes.flexDocument} style={{ marginBottom: 20 }}>
              <span style={{ marginBottom: 10 }}>
                <FontAwesomeIcon
                  icon={faCheck}
                  size="lg"
                  style={{ color: palettes.coastal.core.mediumBlue, marginRight: 20 }}
                />
                {` ${currency.formatBrazilCurrency(action.causeValue ? action.causeValue.toString() : '0')}`}
              </span>
              <span
                onClick={handleRemoveJudicialAction(index)}
                style={{ color: palettes.coastal.core.mediumBlue, cursor: 'pointer' }}
              >
                {i18next.t('remove')}
              </span>
            </div>
          ))}
      </DialogContent>
      <ModalActionButtons
        changeButtonName={i18next.t('saveChanges')}
        closeModal={closeModal}
        disabled={!judicialActions.length && isAddBtnDisable}
        save={updateInfo}
      />
    </EnhancedDialog>
  );
};
